<script setup lang="ts">
import { computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, email } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import SsnInput from '../../Shared/SSNInput.vue'

const { t } = useI18n()
const { umsokn, onLoaded } = useUmsokn()
const props = defineProps<{ disable?: boolean }>()

// ✅ Dynamic validation with conditional activation
const rules = computed(() => ({
  umsokn: {
    coDeptors: Array.isArray(umsokn.value?.coDeptors)
      ? umsokn.value.coDeptors.map(() => ({
          name: { required: requiredIf(() => umsokn.value?.oneLoanTaker === false) },
          ssn: { required: requiredIf(() => umsokn.value?.oneLoanTaker === false) },
          phone: { required: requiredIf(() => umsokn.value?.oneLoanTaker === false) },
          email: {
            required: requiredIf(() => umsokn.value?.oneLoanTaker === false),
            email
          }
        }))
      : []
  }
}))

const v$ = useVuelidate(rules, { umsokn })

onLoaded(() => {
  if (!umsokn.value) return
  if (!Array.isArray(umsokn.value.coDeptors) || umsokn.value.coDeptors.length === 0) {
    umsokn.value.coDeptors = [{ name: '', ssn: '', phone: '', email: '' }]
  }
  if (umsokn.value.oneLoanTaker === undefined) {
    umsokn.value.oneLoanTaker = true
  }
  nextTick(() => v$.value.$reset())
})

// ✅ Add/remove co-debtors
function addCoDeptor() {
  umsokn.value?.coDeptors.push({ name: '', ssn: '', phone: '', email: '' })
}

function removeCoDeptor(index: number) {
  umsokn.value?.coDeptors.splice(index, 1)
  if (umsokn.value && umsokn.value.coDeptors.length === 0) {
    addCoDeptor()
  }
}

// ✅ Expose async validate()
defineExpose({
  async validate() {
    if (Array.isArray(umsokn.value.coDeptors)) {
      umsokn.value.coDeptors.forEach((_: unknown, i: number) => {
        v$.value.umsokn.coDeptors[i].$touch()
      })
    }
    const isValid = await v$.value.$validate()
    return !v$.value.$invalid && isValid
  }
})
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <div class="forms__header">
        {{ t('lan_nafnabeyting_umsokn_step7_header1') }}
      </div>

      <!-- radio buttons -->
      <v-row>
        <v-col cols="12">
          <v-radio-group v-model="umsokn.oneLoanTaker" :readonly="props.disable" hide-details>
            <v-radio
              :label="t('lan_nafnabeyting_umsokn_step7_eini_skuldari')"
              :value="true"
            />
            <v-radio
              :label="t('lan_nafnabeyting_umsokn_step7_fleiri_skuldarar')"
              :value="false"
            />
          </v-radio-group>
        </v-col>
      </v-row>

      <!-- co-debtors list -->
      <transition-group name="list" v-if="umsokn.oneLoanTaker === false">
        <div
          v-for="(deptor, index) in umsokn.coDeptors"
          :key="'coDeptors_' + index"
          class="list-item"
        >
          <div v-if="index > 0" class="forms__divider__container">
            <div class="forms__divider"></div>
            <button
              v-if="!props.disable"
              type="button"
              class="forms__warning__list__wrapper"
              @click="removeCoDeptor(index)"
            >
              <img class="forms__warning__icon" src="@/assets/icons/Close.svg" alt="Remove" />
            </button>
          </div>

          <v-row>
            <!-- Name -->
            <v-col md="6" cols="12">
              <v-text-field
                v-model="deptor.name"
                :readonly="props.disable"
                :label="t('lan_nafnabeyting_umsokn_step7_nafn')"
                variant="outlined"
                density="comfortable"
                :error="v$.umsokn.coDeptors[index]?.name?.$error"
                :error-messages="v$.umsokn.coDeptors[index]?.name?.$dirty
                  ? [
                      v$.umsokn.coDeptors[index]?.name?.required?.$invalid
                        ? t('umsokn_general_required_error')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>

            <!-- SSN -->
            <v-col md="6" cols="12">
              <SsnInput
                v-model="deptor.ssn"
                :readonly="props.disable"
                :label="t('lan_nafnabeyting_umsokn_step7_kennitala')"
                :error="v$.umsokn.coDeptors[index]?.ssn?.$error"
                :error-messages="v$.umsokn.coDeptors[index]?.ssn?.$dirty
                  ? [
                      v$.umsokn.coDeptors[index]?.ssn?.required?.$invalid
                        ? t('umsokn_general_required_error')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Phone -->
            <v-col md="6" cols="12">
              <v-text-field
                v-model="deptor.phone"
                :readonly="props.disable"
                :label="t('lan_nafnabeyting_umsokn_step7_simi')"
                variant="outlined"
                density="comfortable"
                :error="v$.umsokn.coDeptors[index]?.phone?.$error"
                :error-messages="v$.umsokn.coDeptors[index]?.phone?.$dirty
                  ? [
                      v$.umsokn.coDeptors[index]?.phone?.required?.$invalid
                        ? t('umsokn_general_required_error')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>

            <!-- Email -->
            <v-col md="6" cols="12">
              <v-text-field
                v-model="deptor.email"
                :readonly="props.disable"
                :label="t('lan_nafnabeyting_umsokn_step7_email')"
                variant="outlined"
                density="comfortable"
                :error="v$.umsokn.coDeptors[index]?.email?.$error"
                :error-messages="v$.umsokn.coDeptors[index]?.email?.$dirty
                  ? [
                      v$.umsokn.coDeptors[index]?.email?.required?.$invalid
                        ? t('umsokn_general_required_error')
                        : '',
                      v$.umsokn.coDeptors[index]?.email?.email?.$invalid
                        ? t('umsokn_umsaekjandi_netfang_invalid')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>
          </v-row>
        </div>
      </transition-group>

      <div class="m-t-40" v-if="!props.disable && umsokn.oneLoanTaker === false">
         <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          class="mt-4"
          @click="addCoDeptor"
        >
          {{ $t('lan_nafnabeyting_umsokn_step7_add') }}
        </v-btn>
      </div>

      <v-row style="margin-top: 40px;">
        <v-col>
          <p>{{ t('lan_nafnabeyting_umsokn_step7_disclaimer') }}</p>
        </v-col>
      </v-row>
    </v-col>
  </div>
</template>
